import { useQuery } from '@w/react-query'
import { queryOptions } from '@w/react-query/db'
import { Link } from '../router/flashcards-fe.router-bindings'
import { readLearnerActivity } from '../db/learner-actions'
import { pageReadColumns, pageReadSchema, pageMetaColumns, pageMetaSchema } from '../db/page-read'
import { pageBlocks } from '../pages/page-blocks'
import { getCardContent } from './cards-content'
import { traverseBlocks } from '@w/content-blocks/src/traverse'
import type { SpaceResource } from '../router/spaces-resources'

/**
 * The caller's own activity: the full row of every question they touched, and their untouched questions as Not studied
 * (readLearnerActivity). Progress is private: no role opens another member's activity (flashcards-spaces.spec.md
 * spaces-teacher-read-only), so nothing here takes another user's id.
 */
function unstudiedQuestions(pages: ReturnType<ReturnType<typeof pageReadSchema>['parse']>[], unstudied: Set<string>) {
  const questions = new Map<string, { qid: string; pageTitle: string; content: string }>()
  for (const page of pages) {
    if (page.deletedAt !== null) continue
    const blocks = pageBlocks(page.content)
    traverseBlocks(blocks, block => {
      if ((block.type !== 'question' && block.type !== 'cloze') || !block.qid || !unstudied.has(block.qid) || questions.has(block.qid)) return
      questions.set(block.qid, { qid: block.qid, pageTitle: page.title.trim() || 'Untitled page', content: getCardContent(blocks, block.qid) })
    })
  }
  return [...questions.values()]
}

/** Read-only activity derives exclusively from permitted reviews, never local cards or page lifecycle. */
export function MyActivity({ resource }: { resource: SpaceResource }) {
  const workspaceId = resource.space.workspaceId
  const db = resource.db
  const events = useQuery(queryOptions(db.query`SELECT ${db.flashcardEvents.all} FROM ${db.flashcardEvents}
    ORDER BY ${db.flashcardEvents.id}`, db.flashcardEvents.__selectSchema), resource.queryClient)
  const permittedPages = useQuery(queryOptions(db.query`SELECT ${pageMetaColumns(db.pages)} FROM ${db.pages} ORDER BY ${db.pages.id}`, pageMetaSchema(resource.space.id)), resource.queryClient)
  const unstudiedIds = useQuery(queryOptions(db.query`SELECT ${db.pages.id} FROM ${db.pages}
    WHERE ${db.pages.deletedAt.isNull()} AND EXISTS(SELECT 1 FROM crdt_attr_lookup('idx_pages_crdt_attr',NULL,${db.pages}.rowid) parts
      WHERE json_type(parts.fields,'$.qid')='text' AND json_extract(parts.fields,'$.type') IN ('question','cloze') AND json_extract(parts.fields,'$.qid') NOT IN (SELECT qid FROM flashcardEvents WHERE userId=${resource.userId}))`, { id: db.pages.id }), resource.queryClient)
  const displayedPages = useQuery(queryOptions(db.query`SELECT ${pageReadColumns(db.pages)} FROM ${db.pages}
    WHERE ${db.pages.id.inArray(unstudiedIds.data?.map(page => page.id) ?? [])} ORDER BY ${db.pages.id}`, pageReadSchema(resource.space.id)), resource.queryClient)
  const activityOptions = queryOptions(db.query`SELECT ${db.flashcardEvents.qid} FROM ${db.flashcardEvents}
    WHERE ${db.flashcardEvents.userId.eq(resource.userId)}
    UNION SELECT ${db.flashcards.qid} FROM ${db.flashcards}
    WHERE ${db.flashcards.userId.eq(resource.userId)}`, { qid: db.flashcards.qid })
  const activity = useQuery({ ...activityOptions, queryKey: ['my-activity', ...activityOptions.queryKey], queryFn: () => readLearnerActivity(db, resource.userId) }, resource.queryClient)
  const reviews = activity.data?.reviews ?? []
  const latest = activity.data?.latestByQid ?? []
  const notStudied = unstudiedQuestions(displayedPages.data ?? [], new Set(activity.data?.notStudied ?? []))
  function sourceUnavailable(qid: string) {
    if (!permittedPages.data || !events.data) return false
    const readable = new Set(permittedPages.data.map(page => page.id))
    return !activity.data?.latestByQid.some(card=>card.qid===qid && readable.has(card.pageId))
  }
  return <section aria-label="My activity" data-testid="my-activity" className="flex flex-col gap-4 p-4">
    {activity.data && permittedPages.data ? <>
      <h1>My activity</h1>
      <p>Read-only review history</p>
      <p>Reviewed questions: {activity.data?.reviewedQuestionCount ?? 0} · Reviews: {activity.data?.reviewCount ?? 0}</p>
      <table aria-label="Review history"><thead><tr><th>Question</th><th>Score</th><th>Duration</th><th>Time</th></tr></thead>
        <tbody>{reviews.map(review => <tr key={review.eventId}><td><Link to="/$workspaceId/training/$flashcardId" params={{ workspaceId, flashcardId: review.qid }}>{review.qid}</Link></td><td>{review.score}</td><td>{review.durationSeconds}s</td><td>{new Date(review.occurredAt).toLocaleString()}</td></tr>)}</tbody>
      </table>
      <h2>Latest learner card state</h2>
      {latest.map(({ qid, attributes, schedule, frozenAt }) => <div key={qid} className="border-separator flex flex-col gap-2 rounded-lg border p-3">
        <p>{qid}</p>
        {sourceUnavailable(qid) ? <p>Source unavailable</p> : null}
        <p>{attributes.importance} importance · {attributes.difficulty} difficulty · {attributes.promptType}</p>
        <p>Stage {schedule.stage} · Repeat {schedule.repeatAt === 'now' ? 'now' : new Date(schedule.repeatAt).toLocaleString()} · TTA {schedule.ttaSeconds}s · {schedule.graduated ? 'Graduated' : 'Not graduated'} · {frozenAt === null ? 'Not frozen' : 'Frozen'}</p>
      </div>)}
      {notStudied.length > 0 ? <>
        <h2>Not studied</h2>
        <ul aria-label="Not studied">{notStudied.map(({ qid, pageTitle, content }) => <li key={qid}><span>{content}</span> · <span>{pageTitle}</span></li>)}</ul>
      </> : null}
    </> : null}
    {events.error || activity.error || permittedPages.error ? <p role="alert">Activity unavailable: {(events.error ?? activity.error ?? permittedPages.error)?.message}</p> : null}
  </section>
}
